import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getUseMediaList,
  UseMediaListItem,
  UseMediaSearchParams,
  UseMediaAccountLinkStatus,
} from '../../api/useMedia';
import type { RootState } from '../store';
import type { AsyncStatus } from '../../interfaces';

interface UseMediaState {
  status: AsyncStatus;
  list: UseMediaListItem[];
  // Total record count from the API, used for pagination calculation.
  // Page/pageSize are owned by the List component's local state.
  total: number;
}

const initialState: UseMediaState = {
  status: 'idle',
  list: [],
  total: 0,
};

export const fetchUseMediaList = createAsyncThunk(
  'useMedia/fetchList',
  (params: UseMediaSearchParams) => getUseMediaList(params),
  // Skip only if a fetch is already in progress (prevents concurrent duplicate requests).
  { condition: (_, { getState }) => (getState() as RootState).useMedia.status !== 'loading' },
);

const useMediaSlice = createSlice({
  name: 'useMedia',
  initialState,
  reducers: {
    // Reset state so the next mount triggers a fresh fetch.
    reset: () => initialState,
    // Update the link status of an item in the list (optimistic status change).
    updateItemStatus: (
      state,
      action: PayloadAction<{
        useMediaAccountId: number;
        linkStatus: UseMediaAccountLinkStatus;
      }>,
    ) => {
      const item = state.list.find((i) => i.useMediaAccountId === action.payload.useMediaAccountId);
      if (item) {
        item.linkStatus = action.payload.linkStatus;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUseMediaList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUseMediaList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(fetchUseMediaList.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { reset: resetUseMedia, updateItemStatus } = useMediaSlice.actions;
export default useMediaSlice.reducer;
